import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import CustomDatePicker from './CustomDatePicker';
import CustomFileUpload from './CustomFileUpload';
import { MultiRowField } from './MultiRowField';
import { HeaderField } from './HeaderField';
import { Form } from '../types/form';
import { Upload } from 'lucide-react';

interface FormPreviewProps {
  form: Form;
}

const FormPreview: React.FC<FormPreviewProps> = ({ form }) => {
  const renderField = (field: any) => {
    switch (field.field_type) {
      case 'text':
      case 'email':
      case 'phone':
        return (
          <Input
            type={field.field_type === 'email' ? 'email' : 'text'}
            placeholder={field.placeholder}
            disabled
          />
        );
      
      case 'number':
        return (
          <Input
            type="number"
            placeholder={field.placeholder}
            disabled
          />
        );
      
      case 'textarea':
        return (
          <textarea
            className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={4}
            placeholder={field.placeholder}
            disabled
          />
        );
      
      case 'dropdown':
        return (
          <select
            className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled
          >
            <option value="">Select an option</option>
            {Object.entries(field.options || {}).map(([key, value]) => (
              <option key={key} value={key}>
                {value as string}
              </option>
            ))}
          </select>
        );
      
      case 'radio':
        const radioLayout = field.layout_config?.layout || 'vertical';
        const radioLabelBelow = field.layout_config?.labelBelow || false;
        const radioEvenlySpaced = field.layout_config?.evenlySpaced || false;
        return (
          <div className={radioLayout === 'horizontal' ? `flex flex-wrap gap-4 ${radioEvenlySpaced ? 'justify-between' : ''}` : 'space-y-2'}>
            {Object.entries(field.options || {}).filter(([key]) => !['layout', 'labelBelow', 'evenlySpaced'].includes(key)).map(([key, value]) => (
              <div key={key} className={`${radioLayout === 'horizontal' && radioLabelBelow ? 'flex flex-col items-center' : 'flex items-center space-x-2'} ${radioEvenlySpaced && radioLayout === 'horizontal' ? 'flex-1' : ''}`}>
                <input
                  type="radio"
                  name={field.id}
                  value={key}
                  disabled
                  className="w-4 h-4 text-blue-600"
                />
                <label className={`text-sm ${radioLayout === 'horizontal' && radioLabelBelow ? 'mt-2 text-center' : ''}`}>{value as string}</label>
              </div>
            ))}
          </div>
        );
      
      case 'checkbox':
        const checkboxLayout = field.layout_config?.layout || 'vertical';
        const checkboxLabelBelow = field.layout_config?.labelBelow || false;
        const checkboxEvenlySpaced = field.layout_config?.evenlySpaced || false;
        return (
          <div className={checkboxLayout === 'horizontal' ? `flex flex-wrap gap-4 ${checkboxEvenlySpaced ? 'justify-between' : ''}` : 'space-y-2'}>
            {Object.entries(field.options || {}).filter(([key]) => !['layout', 'labelBelow', 'evenlySpaced'].includes(key)).map(([key, value]) => (
              <div key={key} className={`${checkboxLayout === 'horizontal' && checkboxLabelBelow ? 'flex flex-col items-center' : 'flex items-center space-x-2'} ${checkboxEvenlySpaced && checkboxLayout === 'horizontal' ? 'flex-1' : ''}`}>
                <input
                  type="checkbox"
                  value={key}
                  disabled
                  className="w-4 h-4 text-blue-600"
                />
                <label className={`text-sm ${checkboxLayout === 'horizontal' && checkboxLabelBelow ? 'mt-2 text-center' : ''}`}>{value as string}</label>
              </div>
            ))}
          </div>
        );
      
      case 'date':
        return (
          <div className="pointer-events-none">
            <CustomDatePicker
              value=""
              onChange={() => {}}
              placeholder={field.placeholder || "Pick a date"}
            />
          </div>
        );
      
      case 'file':
        return (
          <div className="pointer-events-none">
            <CustomFileUpload
              onChange={() => {}}
              placeholder={field.placeholder || "Choose a file"}
            />
          </div>
        );

      case 'image_upload':
        return (
          <div className="pointer-events-none">
            <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg bg-muted/5">
              <div className="p-8 text-center">
                <Upload className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                <p className="text-sm font-medium text-foreground mb-1">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-muted-foreground">
                  PNG, JPG, GIF up to 10MB
                </p>
              </div>
            </div>
          </div>
        );

      case 'multi_row':
        // Create sample data based on configured columns
        const columns = field.options?.columns || [];
        const sampleRowCount = field.options?.initialRowCount || 2;
        const sampleData = Array(sampleRowCount).fill(null).map(() => 
          columns.map((col: any) => {
            if (col.type === 'checkbox' && col.options?.length) {
              return []; // Empty array for checkbox multi-select
            }
            return ''; // Empty string for other field types
          })
        );
        
        return (
          <div className="pointer-events-none">
            <MultiRowField
              field={field}
              value={sampleData}
              onChange={() => {}}
              disabled={true}
            />
          </div>
        );
      
      case 'image':
        const getImageSizeClass = (size: string) => {
          switch (size) {
            case 'small': return 'max-w-[150px]';
            case 'medium': return 'max-w-[300px]';
            case 'large': return 'max-w-[500px]';
            case 'full': return 'w-full';
            default: return 'max-w-[300px]';
          }
        };
        
        const getImageMaxHeight = (size: string) => {
          switch (size) {
            case 'small': return '150px';
            case 'medium': return '300px';
            case 'large': return '400px';
            case 'full': return '500px';
            default: return '300px';
          }
        };

        if (field.layout_config?.layout === 'side-by-side') {
          return (
            <div className={`flex gap-4 ${getImageSizeClass(field.options?.imageSize || 'medium')}`}>
              {(field.options?.imageUrl || field.options?.imageFile) && (
                <div className="flex-1">
                  <img
                    src={field.options.imageFile || field.options.imageUrl}
                    alt={field.options?.imageAlt || 'Left image'}
                    className="w-full h-auto rounded border object-cover"
                    style={{ maxHeight: getImageMaxHeight(field.options?.imageSize || 'medium') }}
                    onError={(e) => {
                      const img = e.target as HTMLImageElement;
                      img.style.display = 'none';
                    }}
                  />
                </div>
              )}
              {(field.options?.imageUrl2 || field.options?.imageFile2) && (
                <div className="flex-1">
                  <img
                    src={field.options.imageFile2 || field.options.imageUrl2}
                    alt={field.options?.imageAlt2 || 'Right image'}
                    className="w-full h-auto rounded border object-cover"
                    style={{ maxHeight: getImageMaxHeight(field.options?.imageSize || 'medium') }}
                    onError={(e) => {
                      const img = e.target as HTMLImageElement;
                      img.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>
          );
        }
        
        return (field.options?.imageUrl || field.options?.imageFile) ? (
          <div className={getImageSizeClass(field.options?.imageSize || 'medium')}>
            <img
              src={field.options.imageFile || field.options.imageUrl}
              alt={field.options?.imageAlt || 'Form image'}
              className="w-full h-auto rounded border"
              style={{ maxHeight: getImageMaxHeight(field.options?.imageSize || 'medium') }}
              onError={(e) => {
                const img = e.target as HTMLImageElement;
                img.style.display = 'none';
              }}
            />
          </div>
        ) : (
          <div className="w-full p-4 border-2 border-dashed border-gray-300 rounded text-center text-gray-500">
            No image configured
          </div>
        );
      
      case 'header':
        return (
          <HeaderField
            field={field}
          />
        );
      
      case 'logic':
        // Logic fields are hidden in preview/form as they are calculated automatically
        return null;
      
      case 'checkbox_with_input':
        return (
          <div className="space-y-3">
            {Object.entries(field.options || {}).filter(([key]) => key !== 'inputType' && key !== 'inputPlaceholder').map(([key, value]) => (
              <div key={key} className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  disabled
                  className="w-4 h-4 text-blue-600"
                />
                <label className="text-sm min-w-0">{value as string}</label>
                <Input
                  type={field.options?.inputType || 'text'}
                  placeholder={field.options?.inputPlaceholder || 'Enter value'}
                  disabled
                  className="flex-1"
                />
              </div>
            ))}
            {Object.keys(field.options || {}).filter(key => key !== 'inputType' && key !== 'inputPlaceholder').length === 0 && (
              <div className="text-sm text-muted-foreground">No options configured yet</div>
            )}
          </div>
        );
      
      default:
        return (
          <Input
            placeholder={field.placeholder}
            disabled
          />
        );
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">{form.title || 'Untitled Form'}</CardTitle>
          {form.description && (
            <p className="text-muted-foreground mt-2">{form.description}</p>
          )}
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {form.fields.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No fields added yet. Add some fields to see the preview.</p>
              </div>
            ) : (
              form.fields
                .filter(field => field.field_type !== 'logic') // Hide logic fields in preview
                .map((field) => (
                <div key={field.id} className="space-y-2">
                  {field.field_type === 'header' || field.field_type === 'image' ? (
                    renderField(field)
                  ) : (
                    <>
                      <label className="block text-sm font-medium text-foreground">
                        {field.label}
                        {field.is_required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      {renderField(field)}
                    </>
                  )}
                </div>
                ))
            )}
            
            {form.fields.length > 0 && (
              <div className="pt-4">
                <Button className="w-full" disabled>
                  Submit Form
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FormPreview;